import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Radio } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { useC324Query } from './hooks/useC324Query';
import type { ReportRow, CurrencySummary } from '../../services/c324Api';
import './C324Form.css';

const C324Form: React.FC = () => {
  const { loading, data, currencySummary, groupCounts, queried, noData, runQuery, reset } = useC324Query();

  const [reportType, setReportType] = useState<string>('customer');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [customerFrom, setCustomerFrom] = useState('');
  const [customerTo, setCustomerTo] = useState('');

  const handleQuery = () => {
    runQuery({
      report_type: reportType,
      date_from: dateFrom.trim() || undefined,
      date_to: dateTo.trim() || undefined,
      customer_from: customerFrom.trim() || undefined,
      customer_to: customerTo.trim() || undefined,
    });
  };

  const handleReset = () => {
    setReportType('customer');
    setDateFrom('');
    setDateTo('');
    setCustomerFrom('');
    setCustomerTo('');
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const title = reportType === 'date' ? '交運對帳單（日期別）' : '交運對帳單（客戶別）';
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:11px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:2px 5px}th{background:#f0f0f0;text-align:center}.right{text-align:right}@media print{body{margin:10mm}}</style></head><body>
<h2>${title}</h2>
<table><thead><tr><th>客戶代號</th><th>客戶名稱</th><th>交運日期</th><th>交運單號</th><th>品號品名</th><th>幣別</th><th>單位</th><th>單價</th><th>數量</th><th>金額</th><th>折讓數量</th><th>折讓金額</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.FC120_12}</td><td>${r.customer_name}</td><td>${r.FC120_DATE}</td><td>${r.FC120_NO}</td><td>${r.FC120_ITEM}</td><td>${r.FC120_CURR}</td><td>${r.FC120_UNIT}</td><td class="right">${r.FC120_PRICE?.toFixed(2)}</td><td class="right">${r.FC120_QTY}</td><td class="right">${r.FC120_AMT?.toFixed(2)}</td><td class="right">${r.FC131_16 || 0}</td><td class="right">${r.FC131_AMT?.toFixed(2) || '0.00'}</td></tr>`).join('')}
</tbody></table>
<p style="margin-top:8px">共 ${data.length} 筆</p>
</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<ReportRow> = [
    { title: '客戶代號', dataIndex: 'FC120_12', key: 'FC120_12', width: 100 },
    { title: '客戶名稱', dataIndex: 'customer_name', key: 'customer_name', ellipsis: true, width: 140 },
    { title: '交運日期', dataIndex: 'FC120_DATE', key: 'FC120_DATE', width: 100 },
    { title: '交運單號', dataIndex: 'FC120_NO', key: 'FC120_NO', width: 110 },
    { title: '品號品名', dataIndex: 'FC120_ITEM', key: 'FC120_ITEM', ellipsis: true, width: 180 },
    { title: '幣別', dataIndex: 'FC120_CURR', key: 'FC120_CURR', width: 60 },
    { title: '單位', dataIndex: 'FC120_UNIT', key: 'FC120_UNIT', width: 60 },
    { title: '單價', dataIndex: 'FC120_PRICE', key: 'FC120_PRICE', width: 90, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '數量', dataIndex: 'FC120_QTY', key: 'FC120_QTY', width: 80, align: 'right' as const },
    { title: '金額', dataIndex: 'FC120_AMT', key: 'FC120_AMT', width: 100, align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '折讓數量', dataIndex: 'FC131_16', key: 'FC131_16', width: 80, align: 'right' as const, render: (v: number) => v || '' },
    { title: '折讓金額', dataIndex: 'FC131_AMT', key: 'FC131_AMT', width: 100, align: 'right' as const, render: (v: number) => v ? v.toFixed(2) : '' },
  ];

  const summaryColumns: ColumnsType<CurrencySummary> = [
    { title: reportType === 'date' ? '日期' : '客戶', dataIndex: 'group_key', key: 'group_key', width: 120 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 80 },
    { title: '貨款小計', dataIndex: 'goods_amt', key: 'goods_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '稅款小計', dataIndex: 'tax_amt', key: 'tax_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
    { title: '含稅合計', dataIndex: 'total_amt', key: 'total_amt', align: 'right' as const, render: (v: number) => v?.toFixed(2) },
  ];

  const filterLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: 12, color: '#666', marginBottom: 4 }}>{text}</label>
  );

  return (
    <PermissionGuard module={Modules.C324} action="query">
      <div className="c324-container">
        <div className="c324-header">
          <h2>交運對帳單</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            {filterLabel('報表類型')}
            <Radio.Group value={reportType} onChange={e => setReportType(e.target.value)}>
              <Radio.Button value="customer">客戶別</Radio.Button>
              <Radio.Button value="date">日期別</Radio.Button>
            </Radio.Group>
          </div>
          <Space wrap size="middle">
            <div>
              {filterLabel('客戶代號起')}
              <Input value={customerFrom} onChange={e => setCustomerFrom(e.target.value)} placeholder="起" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('客戶代號迄')}
              <Input value={customerTo} onChange={e => setCustomerTo(e.target.value)} placeholder="迄" style={{ width: 120 }} allowClear />
            </div>
            <div>
              {filterLabel('交運日期起 (YYYY-MM-DD)')}
              <Input value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="例 2026-01-01" style={{ width: 140 }} allowClear />
            </div>
            <div>
              {filterLabel('交運日期迄 (YYYY-MM-DD)')}
              <Input value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="例 2026-12-31" style={{ width: 140 }} allowClear />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>查詢</Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && <Empty description="查無此資料" style={{ marginTop: 40 }} />}
        {queried && !noData && (
          <>
            {groupCounts.length > 0 && (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#555' }}>
                {reportType === 'date' ? '日期' : '客戶'}筆數：
                {groupCounts.map(g => `${g.name || g.code}(${g.count})`).join('、')}
              </div>
            )}
            <Table<ReportRow>
              columns={columns}
              dataSource={data}
              rowKey={(r, i) => `${r.FC120_NO}-${r.FC120_SEQ}-${i}`}
              loading={loading}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
              scroll={{ x: 1300 }}
              style={{ marginBottom: 16 }}
            />
            {currencySummary.length > 0 && (
              <>
                <div style={{ marginBottom: 6, fontWeight: 600 }}>
                  {reportType === 'date' ? '日期' : '客戶'}×幣別小計
                </div>
                <Table<CurrencySummary>
                  columns={summaryColumns}
                  dataSource={currencySummary}
                  rowKey={(r, i) => `${r.group_key}-${r.currency}-${i}`}
                  size="small"
                  pagination={false}
                  scroll={{ x: 600 }}
                />
              </>
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
};

export default C324Form;
